import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ChatMessage, { TypingIndicator } from "./ChatMessage";
import { sanitizeArtifactContent } from "../utils/sanitizeArtifact";
import type { ChatMessage as ChatMessageType, PipelineReviewStageInfo } from "../lib/types";

const CHECKLIST_LABELS: Record<string, string> = {
  business_case: "Business Case",
  product_spec: "Product Specification",
  architecture: "Technical Architecture",
  epic_plan: "Epic Plan",
};

const STAGE_LABELS: Record<string, string> = {
  business_case: "Business Case",
  product_spec: "Product Specification",
  architecture: "Technical Architecture",
  epic_plan: "Epic Plan",
};

const APPROVE_LABELS: Record<string, string> = {
  business_case: "Approve & Generate Product Spec",
  product_spec: "Approve & Generate Architecture",
  architecture: "Approve & Generate Epic Plan",
  epic_plan: "Approve & Complete Pipeline",
};

const CHALLENGE_PLACEHOLDERS: Record<string, string> = {
  business_case: "Challenge the analysis or push back on assumptions...",
  product_spec: "Question the scope, priorities, or missing features...",
  architecture: "Challenge technical decisions or raise concerns...",
  epic_plan: "Question the plan, timelines, or priorities...",
};

/**
 * Extract the first major section content from a markdown document.
 * Looks for Executive Summary, Product Vision, Overview, or falls back
 * to the first ## section content.
 */
function extractLeadSection(markdown: string): string {
  const clean = markdown
    .replace(/^---BEGIN [A-Z\s]+---\n?/m, "")
    .replace(/\n?---END [A-Z\s]+---$/m, "")
    .trim();

  // Try known lead section headings
  const patterns = [
    /##\s*Executive Summary\s*\n+([\s\S]*?)(?=\n##\s|\n#\s|$)/i,
    /##\s*Product Vision\s*\n+([\s\S]*?)(?=\n##\s|\n#\s|$)/i,
    /##\s*Overview\s*\n+([\s\S]*?)(?=\n##\s|\n#\s|$)/i,
  ];

  for (const pattern of patterns) {
    const match = clean.match(pattern);
    if (match) return match[1].trim();
  }

  // Fallback: first ## section content
  const fallback = clean.match(/##\s+.+\s*\n+([\s\S]*?)(?=\n##\s|\n#\s|$)/);
  if (fallback) return fallback[1].trim();

  return "";
}

interface StageReviewProps {
  stage: string;
  stageInfo: PipelineReviewStageInfo;
  isGenerating: boolean;
  isLast: boolean;
  onSendMessage: (message: string) => Promise<void>;
  onApprove: () => Promise<void>;
  onDownloadPdf?: () => Promise<void>;
  sendingMessage: boolean;
  readOnly?: boolean;
  staleMessage?: string | null;
}

export default function StageReview({
  stage,
  stageInfo,
  isGenerating,
  isLast,
  onSendMessage,
  onApprove,
  onDownloadPdf,
  sendingMessage,
  readOnly,
  staleMessage,
}: StageReviewProps) {
  const [approving, setApproving] = useState(false);
  const [documentExpanded, setDocumentExpanded] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);

  const artifact = stageInfo.artifact_content || null;
  const messages = stageInfo.review_messages || [];
  const leadSection = artifact ? extractLeadSection(artifact) : "";

  // Extract initial AI challenge message (first assistant message in review conversation)
  // TODO: If the backend doesn't auto-generate an initial challenge on stage review entry,
  // this will be empty. The backend pipeline_review.py would need to seed an initial message.
  const initialChallenge = messages.find((m) => m.role === "assistant");
  const conversationMessages = initialChallenge ? messages.filter((m) => m !== initialChallenge) : messages;

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sendingMessage]);

  async function handleApprove() {
    setApproving(true);
    try {
      await onApprove();
    } finally {
      setApproving(false);
    }
  }

  async function handleDownloadPdf() {
    if (!onDownloadPdf) return;
    setDownloadingPdf(true);
    try {
      await onDownloadPdf();
    } finally {
      setDownloadingPdf(false);
    }
  }

  return (
    <div className="flex flex-col gap-4 min-h-0 flex-1">
      {/* 1. Header row: stage name + Expand Document + Download PDF */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">
          {STAGE_LABELS[stage] || stage}
        </h3>
        <div className="flex items-center gap-2">
          {artifact && (
            <button
              onClick={() => setDocumentExpanded(!documentExpanded)}
              className="inline-flex items-center gap-1.5 bg-slate-700 hover:bg-slate-600 text-white text-xs font-medium rounded-lg px-3 py-1.5 transition-colors"
            >
              <svg
                className={`w-3.5 h-3.5 transition-transform ${documentExpanded ? "rotate-180" : ""}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
              {documentExpanded ? "Collapse Document" : "Expand Document"}
            </button>
          )}
          {onDownloadPdf && artifact && (
            <button
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white text-xs font-medium rounded-lg px-3 py-1.5 transition-colors"
            >
              {downloadingPdf ? (
                <>
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download PDF
                </>
              )}
            </button>
          )}
          {stageInfo.generation_count > 1 && (
            <span className="text-xs text-slate-500">
              v{stageInfo.generation_count}
            </span>
          )}
        </div>
      </div>

      {/* 2. Document content — generating overlay sits on top */}
      <div className="flex flex-col bg-slate-900/60 border border-slate-700/40 rounded-xl relative">
        {isGenerating && (
          <GeneratingOverlay
            currentStage={stage}
            staleMessage={staleMessage}
            generationStatus={stageInfo.generation_status}
          />
        )}

        {/* Full document panel (collapsed by default) */}
        {documentExpanded && artifact && (
          <div className="p-4 sm:p-6 border-b border-slate-700/40">
            <div className="prose prose-invert prose-sm max-w-none prose-headings:text-white prose-p:text-slate-300 prose-li:text-slate-300 prose-strong:text-white prose-table:text-sm">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{sanitizeArtifactContent(artifact)}</ReactMarkdown>
            </div>
            <div className="flex justify-center mt-4">
              <button
                onClick={() => setDocumentExpanded(false)}
                className="inline-flex items-center gap-1.5 bg-slate-700 hover:bg-slate-600 text-white text-xs font-medium rounded-lg px-3 py-1.5 transition-colors"
              >
                <svg
                  className="w-3.5 h-3.5 rotate-180"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
                Collapse Document
              </button>
            </div>
          </div>
        )}

        {/* Executive summary / lead section (always visible when not generating) */}
        {artifact && !documentExpanded && (
          <div className="p-4 sm:p-6">
            {leadSection ? (
              <div className="prose prose-invert prose-sm max-w-none prose-headings:text-white prose-p:text-slate-300 prose-li:text-slate-300 prose-strong:text-white prose-table:text-sm">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{leadSection}</ReactMarkdown>
              </div>
            ) : (
              <div className="prose prose-invert prose-sm max-w-none prose-headings:text-white prose-p:text-slate-300 prose-li:text-slate-300 prose-strong:text-white prose-table:text-sm">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{sanitizeArtifactContent(artifact)}</ReactMarkdown>
              </div>
            )}
          </div>
        )}

        {/* No artifact yet */}
        {!artifact && !isGenerating && (
          <div className="flex items-center justify-center h-32 text-slate-500 text-sm">
            No artifact generated yet.
          </div>
        )}
      </div>

      {/* 3. Architect's Analysis callout */}
      {initialChallenge && !isGenerating && (
        <div className="bg-slate-800/80 border border-blue-500/20 rounded-xl p-4 sm:p-5">
          <div className="flex items-start gap-2 mb-2">
            <svg className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            <span className="text-xs font-semibold text-blue-400 uppercase tracking-wide">Architect's Analysis</span>
          </div>
          <div className="prose prose-invert prose-sm max-w-none prose-p:text-slate-300 prose-li:text-slate-300 prose-strong:text-white">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{initialChallenge.content}</ReactMarkdown>
          </div>
        </div>
      )}

      {/* 4. Chat messages */}
      {!isGenerating && artifact && (conversationMessages.length > 0 || sendingMessage) && (
        <div className="space-y-1 max-h-[400px] overflow-y-auto">
          {conversationMessages.map((msg: ChatMessageType, i: number) => (
            <ChatMessage key={i} role={msg.role} content={msg.content} />
          ))}
          {sendingMessage && <TypingIndicator />}
          <div ref={chatEndRef} />
        </div>
      )}

      {/* 5. Chat input */}
      {!readOnly && !isGenerating && artifact && (
        <ChatInputInline
          ref={chatInputRef}
          onSend={onSendMessage}
          disabled={sendingMessage || isGenerating}
          placeholder={CHALLENGE_PLACEHOLDERS[stage] || "Ask a question..."}
        />
      )}

      {/* 6. Approve button (final action) */}
      {!readOnly && !isGenerating && artifact && (
        <button
          onClick={handleApprove}
          disabled={approving || isGenerating || sendingMessage}
          className="w-full bg-green-600 hover:bg-green-500 disabled:bg-slate-700 disabled:text-slate-500 text-white text-sm font-medium rounded-lg py-2.5 transition-colors"
        >
          {approving
            ? "Approving..."
            : APPROVE_LABELS[stage] || (isLast ? "Approve & Complete" : "Approve & Continue")}
        </button>
      )}

      {readOnly && (
        <div className="text-xs text-slate-500 text-center py-2">
          This stage has been approved. Click the active stage to continue.
        </div>
      )}
    </div>
  );
}

/** Overlay shown while a stage is generating — spinner with elapsed timer */
function GeneratingOverlay({
  currentStage,
  staleMessage,
  generationStatus,
}: {
  currentStage: string;
  staleMessage?: string | null;
  generationStatus?: { message: string; started_at: string } | null;
}) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    function computeElapsed() {
      if (generationStatus?.started_at) {
        const start = new Date(generationStatus.started_at).getTime();
        return Math.max(0, Math.floor((Date.now() - start) / 1000));
      }
      return 0;
    }
    setElapsed(computeElapsed());
    const interval = setInterval(() => setElapsed(computeElapsed()), 1000);
    return () => clearInterval(interval);
  }, [generationStatus?.started_at]);

  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;
  const timeStr = minutes > 0
    ? `${minutes}m ${seconds.toString().padStart(2, "0")}s`
    : `${seconds}s`;

  const displayMessage = generationStatus?.message
    || `Generating ${CHECKLIST_LABELS[currentStage] || currentStage}...`;

  return (
    <div className="absolute inset-0 bg-slate-900/80 rounded-xl flex items-center justify-center z-10">
      <div className="flex flex-col items-center gap-3 px-6">
        <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        <span data-testid="generating-label" className="text-sm text-blue-300 text-center">
          {displayMessage}
        </span>
        {generationStatus?.started_at && (
          <span data-testid="elapsed-timer" className="text-xs text-slate-400">
            Elapsed: {timeStr}
          </span>
        )}

        {/* Stale message */}
        {staleMessage && (
          <p data-testid="stale-message" className="text-xs text-amber-400/80 text-center">
            {staleMessage}
          </p>
        )}
      </div>
    </div>
  );
}

/** Inline chat input (lighter version without the border-top Layout wrapper) */
const ChatInputInline = forwardRef<HTMLTextAreaElement, {
  onSend: (message: string) => Promise<void>;
  disabled: boolean;
  placeholder: string;
}>(function ChatInputInline({ onSend, disabled, placeholder }, ref) {
  const [text, setText] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement);

  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = "auto";
      ta.style.height = Math.min(ta.scrollHeight, 120) + "px";
    }
  }, [text]);

  async function handleSubmit() {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    setText("");
    await onSend(trimmed);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }

  return (
    <div className="flex items-end gap-2">
      <textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder={placeholder}
        rows={1}
        className="flex-1 bg-slate-800 text-slate-100 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/50 placeholder-slate-500 disabled:opacity-50"
      />
      <button
        onClick={handleSubmit}
        disabled={disabled || !text.trim()}
        className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg px-3 py-2 text-sm font-medium transition-colors flex-shrink-0"
      >
        Send
      </button>
    </div>
  );
});
